import { useParams, Link } from 'react-router-dom';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SchemaOrg from '@/components/feature/SchemaOrg';
import { categories, manhwaList } from '@/mocks/manhwa';

export default function Article() {
  const { id } = useParams<{ id: string }>();
  const manhwa = manhwaList.find((m) => m.id === Number(id));
  const category = categories.find((c) => c.slug === manhwa?.category);

  if (!manhwa) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-20">
        <div className="text-center">
          <h1 className="text-2xl font-heading font-bold text-foreground-950 mb-3">漫画不存在</h1>
          <Link to="/" className="text-primary-500 hover:text-primary-600 text-sm whitespace-nowrap cursor-pointer">返回首页</Link>
        </div>
      </div>
    );
  }

  const relatedManhwa = manhwaList
    .filter((m) => m.category === manhwa.category && m.id !== manhwa.id)
    .slice(0, 4);

  return (
    <>
      <SchemaOrg type="WebSite" />
      <SchemaOrg type="Organization" />
      <SchemaOrg type="Article" data={{
        headline: manhwa.title,
        description: manhwa.description,
        author: { '@type': 'Person', name: manhwa.author },
        datePublished: manhwa.chapters[0]?.updateTime || manhwa.updateTime,
        dateModified: manhwa.updateTime,
        image: manhwa.cover,
        url: `https://bluefocugroup.cn/article/${manhwa.id}`,
      }} />

      <Breadcrumb
        items={[
          { label: category?.name || '漫画', href: `/category/${manhwa.category}` },
          { label: manhwa.title },
        ]}
      />

      <main className="max-w-7xl mx-auto px-4 md:px-6 pb-16">
        {/* Article Header */}
        <section className="py-8 md:py-12">
          <div className="flex flex-col md:flex-row gap-8">
            {/* Cover */}
            <div className="w-full md:w-64 flex-shrink-0">
              <div className="aspect-[3/4] rounded-xl overflow-hidden border border-background-200/70">
                <img
                  src={manhwa.cover}
                  alt={manhwa.title}
                  className="w-full h-full object-cover"
                />
              </div>
            </div>

            {/* Info */}
            <div className="flex-1 min-w-0">
              <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-3">{manhwa.title}</h1>

              <div className="flex flex-wrap items-center gap-3 mb-4">
                <span className="flex items-center gap-1 text-sm">
                  <i className="ri-star-fill text-accent-500"></i>
                  <span className="font-semibold text-foreground-800">{manhwa.rating}</span>
                </span>
                <span className="text-foreground-300">|</span>
                <span className="text-sm text-foreground-600">作者：{manhwa.author}</span>
                <span className="text-foreground-300">|</span>
                <span className={`text-sm px-2 py-0.5 rounded-full ${manhwa.status === '连载中' ? 'bg-accent-100 text-accent-700' : 'bg-secondary-100 text-secondary-700'} whitespace-nowrap`}>
                  {manhwa.status}
                </span>
              </div>

              <div className="flex flex-wrap items-center gap-2 mb-4 text-sm text-foreground-500">
                <span className="flex items-center gap-1">
                  <i className="ri-eye-line"></i>
                  {(manhwa.views / 10000).toFixed(1)}万次阅读
                </span>
                <span className="text-foreground-300">|</span>
                <span>更新于 {manhwa.updateTime}</span>
                <span className="text-foreground-300">|</span>
                <span>共 {manhwa.chapters.length} 话</span>
              </div>

              <div className="flex flex-wrap gap-2 mb-5">
                {manhwa.tags.map((tag) => (
                  <span key={tag} className="px-3 py-1 rounded-full bg-background-100 text-xs text-foreground-600 border border-background-200/70 whitespace-nowrap">
                    {tag}
                  </span>
                ))}
              </div>

              <p className="text-sm text-foreground-600 leading-relaxed mb-6">{manhwa.longDescription}</p>

              <div className="flex flex-col sm:flex-row gap-3">
                <Link
                  to={`/article/${manhwa.id}?chapter=1`}
                  className="px-6 py-3 rounded-lg bg-primary-500 text-white text-sm font-medium hover:bg-primary-600 text-center transition-colors whitespace-nowrap cursor-pointer"
                >
                  从第一话开始阅读
                </Link>
                {manhwa.chapters.length > 0 && (
                  <Link
                    to={`/article/${manhwa.id}?chapter=${manhwa.chapters.length}`}
                    className="px-6 py-3 rounded-lg border border-primary-300 text-primary-600 text-sm font-medium hover:bg-primary-50 text-center transition-colors whitespace-nowrap cursor-pointer"
                  >
                    阅读最新话
                  </Link>
                )}
              </div>
            </div>
          </div>
        </section>

        {/* Chapter List */}
        <section className="py-8">
          <h2 className="text-xl font-heading font-bold text-foreground-950 mb-5">章节列表</h2>
          <div className="bg-background-50 rounded-xl border border-background-200/70 overflow-hidden">
            <div className="divide-y divide-background-200/70">
              {manhwa.chapters.map((chapter, index) => (
                <div
                  key={chapter.id}
                  className="flex items-center justify-between px-5 py-4 hover:bg-background-100 transition-colors cursor-pointer"
                >
                  <div className="flex items-center gap-4 min-w-0">
                    <span className="text-xs font-medium text-foreground-400 w-10 flex-shrink-0">No.{String(chapter.number).padStart(2, '0')}</span>
                    <span className="text-sm text-foreground-800 font-medium truncate">{chapter.title}</span>
                  </div>
                  <div className="flex items-center gap-4 flex-shrink-0">
                    <span className="text-xs text-foreground-400">{chapter.updateTime}</span>
                    <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-background-100 text-foreground-400 hover:bg-primary-50 hover:text-primary-500 transition-colors cursor-pointer">
                      <i className="ri-play-circle-line text-lg"></i>
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Related Manhwa */}
        {relatedManhwa.length > 0 && (
          <section className="py-8">
            <h2 className="text-xl font-heading font-bold text-foreground-950 mb-5">同类推荐</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-5">
              {relatedManhwa.map((m) => (
                <Link
                  key={m.id}
                  to={`/article/${m.id}`}
                  className="group bg-background-50 rounded-xl overflow-hidden border border-background-200/70 hover:border-primary-200 transition-all duration-200 cursor-pointer"
                >
                  <div className="aspect-[3/4] overflow-hidden">
                    <img src={m.cover} alt={m.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" loading="lazy" />
                  </div>
                  <div className="p-4">
                    <h3 className="text-sm font-semibold text-foreground-950 line-clamp-1">{m.title}</h3>
                    <div className="flex items-center gap-2 text-xs text-foreground-500 mt-1">
                      <span className="flex items-center gap-1"><i className="ri-star-fill text-accent-500 text-xs"></i>{m.rating}</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}
      </main>
    </>
  );
}